import { z } from 'zod';
import modelRoutingJson from './bedrock-model-routing.json';
import modelAvailabilityJson from './bedrock-model-availability.json';

const BedrockModelRouteSchema = z.object({
  region: z.string().min(1),
  modelId: z.string().min(1),
  inferenceProfileArn: z.string().min(1).optional(),
});

const BedrockModelPricingSchema = z.object({
  currency: z.string().min(1).default('USD'),
  unit: z.literal('1k_tokens').default('1k_tokens'),
  input: z.number().nonnegative().default(0),
  output: z.number().nonnegative().default(0),
});

const BedrockModelSelectionSchema = z.object({
  enabled: z.boolean().default(true),
  order: z.number().int().default(0),
  recommended: z.boolean().default(false),
  badges: z.array(z.string().min(1)).default([]),
});

const BedrockApprovedModelSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  provider: z.string().min(1),
  runtime: z.enum(['anthropic', 'converse']),
  contextWindowTokens: z.number().int().positive(),
  defaultMaxOutputTokens: z.number().int().positive().default(1200),
  selection: BedrockModelSelectionSchema,
  pricing: BedrockModelPricingSchema,
  routes: z.array(BedrockModelRouteSchema).min(1),
});

const BedrockModelRoutingConfigSchema = z.object({
  registryFormat: z.string().min(1),
  registryVersion: z.string().min(1),
  lastReviewedAt: z.string().min(1).optional(),
  defaultRegion: z.string().min(1),
  regionPriorityOrder: z.array(z.string().min(1)).min(1),
  defaultModelKey: z.string().min(1),
  approvedModels: z.array(BedrockApprovedModelSchema).min(1),
  updateCheck: z.object({
    enabled: z.boolean().default(true),
    script: z.string().min(1),
    expectedRegions: z.array(z.string().min(1)).default([]),
  }).optional(),
});

const BedrockModelAvailabilityModelSchema = z.object({
  key: z.string().min(1),
  available: z.boolean(),
  reason: z.string().optional(),
}).passthrough();

const BedrockModelAvailabilitySchema = z.object({
  registryFormat: z.string().min(1),
  registryVersion: z.string().min(1),
  generatedAt: z.string().min(1).optional(),
  sourceStatus: z.enum(['ok', 'degraded-partial', 'degraded-no-aws']).default('ok'),
  sourceReason: z.string().nullable().optional(),
  availableModelKeys: z.array(z.string().min(1)).default([]),
  unavailableModelKeys: z.array(z.string().min(1)).default([]),
  models: z.array(BedrockModelAvailabilityModelSchema).default([]),
  summary: z.object({
    checkedRegions: z.number().int().nonnegative().optional(),
    awsErrors: z.number().int().nonnegative().optional(),
    checkedRoutes: z.number().int().nonnegative().optional(),
    availableModels: z.number().int().nonnegative().optional(),
    unavailableModels: z.number().int().nonnegative().optional(),
  }).optional(),
}).passthrough();

export type BedrockModelRoute = z.infer<typeof BedrockModelRouteSchema>;
export type BedrockApprovedModel = z.infer<typeof BedrockApprovedModelSchema>;
export type BedrockModelRoutingConfig = z.infer<typeof BedrockModelRoutingConfigSchema>;
export type BedrockModelAvailability = z.infer<typeof BedrockModelAvailabilitySchema>;

export type BedrockModelSelectionOption = {
  value: string;
  label: string;
  badges: string[];
};

export type ResolvedBedrockModel = {
  model: BedrockApprovedModel;
  route: BedrockModelRoute;
  requestedKey?: string;
  selectedKey: string;
  requestedSelectable: boolean;
  availabilitySourceStatus: BedrockModelAvailability['sourceStatus'];
};

const parsed = BedrockModelRoutingConfigSchema.parse(modelRoutingJson);
const ENV_ROUTE_PREFIX = 'env:';

const readRuntimeEnvVar = (key: string): string | undefined => {
  const processEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  const processValue = processEnv?.[key];
  if (typeof processValue === 'string' && processValue.trim().length > 0) {
    return processValue.trim();
  }

  return undefined;
};

const bySelectionOrder = (a: BedrockApprovedModel, b: BedrockApprovedModel): number =>
  (a.selection.order - b.selection.order) || a.label.localeCompare(b.label);

const resolveRouteModelId = (configuredModelId: string): string | null => {
  const trimmed = configuredModelId.trim();
  if (!trimmed) return null;
  if (!trimmed.toLowerCase().startsWith(ENV_ROUTE_PREFIX)) {
    return trimmed;
  }
  const envVarName = trimmed.slice(ENV_ROUTE_PREFIX.length).trim();
  if (!envVarName) return null;
  const envValue = readRuntimeEnvVar(envVarName);
  return envValue && envValue.length > 0 ? envValue : null;
};

const resolveRoute = (route: BedrockModelRoute): BedrockModelRoute | null => {
  const resolvedModelId = resolveRouteModelId(route.modelId);
  if (!resolvedModelId) return null;
  if (resolvedModelId === route.modelId) return route;
  return {
    ...route,
    modelId: resolvedModelId,
  };
};

const resolveRoutesForModel = (model: BedrockApprovedModel): BedrockModelRoute[] =>
  model.routes
    .map(resolveRoute)
    .filter((route): route is BedrockModelRoute => Boolean(route));

const hasRoutableRoute = (model: BedrockApprovedModel): boolean =>
  resolveRoutesForModel(model).length > 0;

const modelUsesEnvRoutes = (model: BedrockApprovedModel): boolean =>
  model.routes.some((route) => route.modelId.trim().toLowerCase().startsWith(ENV_ROUTE_PREFIX));

const enabledModels = parsed.approvedModels.filter((model) => model.selection.enabled);
const orderedEnabledModels = [...enabledModels].sort(bySelectionOrder);
const orderedEnabledRoutableModels = orderedEnabledModels.filter(hasRoutableRoute);

const defaultAvailability: BedrockModelAvailability = {
  registryFormat: 'triedge.ai-assistant.bedrock-model-availability',
  registryVersion: '1.0.0',
  sourceStatus: 'degraded-no-aws',
  sourceReason: 'invalid-or-missing-artifact',
  availableModelKeys: orderedEnabledRoutableModels.map((model) => model.key),
  unavailableModelKeys: [],
  models: orderedEnabledRoutableModels.map((model) => ({
    key: model.key,
    available: true,
    reason: 'enabled-default-model',
  })),
  summary: {
    checkedRegions: 0,
    awsErrors: 1,
    checkedRoutes: 0,
    availableModels: orderedEnabledRoutableModels.length,
    unavailableModels: 0,
  },
};

const availabilityParseResult = BedrockModelAvailabilitySchema.safeParse(modelAvailabilityJson);
const availability = availabilityParseResult.success
  ? availabilityParseResult.data
  : defaultAvailability;

const availableModelKeySet = new Set(
  availability.availableModelKeys.map((key) => key.trim().toLowerCase()).filter(Boolean)
);
const knownAvailabilityModelKeySet = new Set(
  availability.models.map((model) => model.key.trim().toLowerCase()).filter(Boolean)
);
const orderedEnabledAvailableModels = orderedEnabledModels.filter((model) => {
  if (!hasRoutableRoute(model)) {
    return false;
  }
  // Env-routed models are runtime-configured; availability artifacts can be stale when env vars change.
  if (modelUsesEnvRoutes(model)) {
    return true;
  }
  const normalizedKey = model.key.toLowerCase();
  // Newly introduced enabled models may not yet appear in the last availability artifact.
  // Treat unknown keys as selectable until the next availability sync runs.
  if (!knownAvailabilityModelKeySet.has(normalizedKey)) {
    return true;
  }
  return availableModelKeySet.has(normalizedKey);
});
const orderedSelectableModels = orderedEnabledAvailableModels.length > 0
  ? orderedEnabledAvailableModels
  : (orderedEnabledRoutableModels.length > 0 ? orderedEnabledRoutableModels : orderedEnabledModels);
const selectableModelKeySet = new Set(
  orderedSelectableModels.map((model) => model.key.toLowerCase())
);

const findModelByKey = (key?: string): BedrockApprovedModel | undefined => {
  const normalized = key?.trim().toLowerCase();
  if (!normalized) return undefined;
  return parsed.approvedModels.find((model) => model.key.toLowerCase() === normalized);
};

const isSelectableModel = (model?: BedrockApprovedModel): model is BedrockApprovedModel =>
  Boolean(model && selectableModelKeySet.has(model.key.toLowerCase()));

const dedupeRegions = (preferredRegion?: string): string[] => {
  const ordered = [
    preferredRegion,
    parsed.defaultRegion,
    ...parsed.regionPriorityOrder,
  ].filter((value): value is string => Boolean(value && value.trim()));

  const seen = new Set<string>();
  const result: string[] = [];
  for (const region of ordered) {
    if (seen.has(region)) continue;
    seen.add(region);
    result.push(region);
  }
  return result;
};

const resolveRouteForModel = (model: BedrockApprovedModel, preferredRegion?: string): BedrockModelRoute => {
  const routableRoutes = resolveRoutesForModel(model);
  const routes = routableRoutes.length > 0 ? routableRoutes : model.routes;
  const regionOrder = dedupeRegions(preferredRegion);
  for (const region of regionOrder) {
    const route = routes.find((candidate) => candidate.region === region);
    if (route) return route;
  }
  return routes[0];
};

export const ADMISSIONS_BEDROCK_MODEL_ROUTING_CONFIG: BedrockModelRoutingConfig = parsed;
export const ADMISSIONS_BEDROCK_MODEL_AVAILABILITY: BedrockModelAvailability = availability;

export const ADMISSIONS_BEDROCK_MODEL_SELECTION_OPTIONS: BedrockModelSelectionOption[] =
  orderedSelectableModels.map((model) => ({
    value: model.key,
    label: model.label,
    badges: model.selection.badges,
  }));

export const ADMISSIONS_BEDROCK_MODEL_KEYS: string[] =
  ADMISSIONS_BEDROCK_MODEL_SELECTION_OPTIONS.map((option) => option.value);

export const ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY: string = (() => {
  const preferred = findModelByKey(parsed.defaultModelKey);
  if (isSelectableModel(preferred)) {
    return preferred.key;
  }
  if (orderedSelectableModels.length > 0) {
    return orderedSelectableModels[0].key;
  }
  return parsed.approvedModels[0].key;
})();

export const resolveAdmissionsBedrockModel = (
  requestedModelKey: string | undefined,
  preferredRegion: string | undefined,
): ResolvedBedrockModel => {
  const requested = findModelByKey(requestedModelKey);
  const requestedSelectable = isSelectableModel(requested);
  const defaultModel = findModelByKey(ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY);
  const model = (requestedSelectable ? requested : undefined)
    ?? (isSelectableModel(defaultModel) ? defaultModel : undefined)
    ?? orderedSelectableModels[0]
    ?? parsed.approvedModels[0];

  const route = resolveRouteForModel(model, preferredRegion);

  return {
    model,
    route,
    requestedKey: requestedModelKey,
    selectedKey: model.key,
    requestedSelectable,
    availabilitySourceStatus: availability.sourceStatus,
  };
};
